import React from "react";
import { Anchor, Container, Compass, Ship, Layers } from "lucide-react";

/* ------------------ Hero Dashboard (Freight Workspace mockup) ------------------ */
export const HeroDashboard = ({ testIds: T }) => {
  return (
    <div
      data-testid={T.heroDashboard}
      className="relative mx-auto w-full max-w-[820px] lg:ml-auto"
    >
      {/* Floating ambient elements */}
      <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#FF6B35]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-[#0A1128]/5 blur-3xl pointer-events-none" />

      {/* Main workspace card */}
      <div className="relative overflow-hidden rounded-[1.9rem] border border-slate-200 bg-white shadow-[0_40px_90px_-34px_rgba(10,17,40,0.25)]">
        {/* Top status bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50/60">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <span className="ml-3 fiq-overline text-slate-500">demaxtore · freightiq</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500 font-mono">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 fiq-pulse-dot" />
            Live
          </div>
        </div>

        <div className="grid grid-cols-12 gap-0">
          {/* Left: shipment header + map + timeline */}
          <div className="col-span-12 border-b border-slate-200 p-5 sm:p-6 lg:col-span-7 lg:border-b-0 lg:border-r lg:p-7">
            {/* Shipment header */}
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="fiq-overline text-slate-400 mb-1.5">Active Shipment</div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3
                    data-testid={T.heroDashboardShipment}
                    className="font-display text-2xl font-bold tracking-tight text-[#0A1128]"
                  >
                    SH-2481
                  </h3>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 fiq-pulse-dot" />
                    At Sea
                  </span>
                </div>
                <div
                  data-testid={T.heroDashboardRoute}
                  className="text-sm text-slate-500 mt-1 font-mono"
                >
                  Mersin (TR) → Mombasa (KE)
                </div>
              </div>
              <div className="text-left sm:text-right">
                <div className="fiq-overline text-slate-400 mb-1">ETA</div>
                <div
                  data-testid={T.heroDashboardEta}
                  className="font-display text-lg font-semibold text-[#0A1128]"
                >
                  12 June
                </div>
              </div>
            </div>

            {/* Metric strip */}
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50/40 px-3 py-2.5">
                <div className="fiq-overline text-slate-400 mb-1">Carrier</div>
                <div data-testid={T.heroDashboardCarrier} className="text-sm font-semibold text-[#0A1128]">MSC</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50/40 px-3 py-2.5">
                <div className="fiq-overline text-slate-400 mb-1">Container</div>
                <div data-testid={T.heroDashboardContainer} className="text-sm font-mono font-semibold text-[#0A1128]">MSCU1234567</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50/40 px-3 py-2.5">
                <div className="fiq-overline text-slate-400 mb-1">Booking</div>
                <div className="text-sm font-semibold text-emerald-700">Confirmed</div>
              </div>
            </div>

            {/* Route visual */}
            <div className="relative overflow-hidden rounded-[1.25rem] border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 sm:p-5">
              <div className="absolute inset-0 fiq-grid-bg opacity-40 pointer-events-none" />
              <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-full bg-[#0A1128] text-white flex items-center justify-center">
                    <Anchor className="w-4 h-4" />
                  </div>
                  <div className="mt-2 text-xs font-semibold text-[#0A1128]">Mersin</div>
                  <div className="text-[10px] text-slate-400 font-mono">TR · 03 Jun</div>
                </div>
                <div className="relative h-12 flex-1 sm:mx-3 sm:h-px">
                  <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 border-l border-dashed border-slate-300 sm:left-0 sm:top-auto sm:h-px sm:w-full sm:translate-x-0 sm:border-l-0 sm:border-t" />
                  <div className="absolute left-1/2 top-0 h-2/3 w-px -translate-x-1/2 bg-[#FF6B35] sm:left-0 sm:top-0 sm:h-px sm:w-2/3 sm:translate-x-0" />
                  <div className="absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 sm:left-2/3 sm:top-0 sm:-translate-y-1/2">
                    <Ship className="w-5 h-5 text-[#0A1128] fiq-vessel-move" />
                  </div>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-full bg-white border-2 border-[#0A1128] text-[#0A1128] flex items-center justify-center">
                    <Anchor className="w-4 h-4" />
                  </div>
                  <div className="mt-2 text-xs font-semibold text-[#0A1128]">Mombasa</div>
                  <div className="text-[10px] text-slate-400 font-mono">KE · 12 Jun</div>
                </div>
              </div>

              {/* Mini timeline */}
              <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-1">
                {[
                  { l: "Booked", ok: true },
                  { l: "Loaded", ok: true },
                  { l: "At Sea", ok: true, active: true },
                  { l: "Arrival", ok: false },
                ].map((s, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    <div
                      className={`h-1 rounded-full ${
                        s.ok ? (s.active ? "bg-[#FF6B35]" : "bg-[#0A1128]") : "bg-slate-200"
                      } ${s.active ? "relative overflow-hidden" : ""}`}
                    >
                      {s.active && <div className="absolute inset-0 fiq-shimmer" />}
                    </div>
                    <div
                      className={`text-[10px] font-mono ${
                        s.active ? "text-[#FF6B35] font-semibold" : "text-slate-500"
                      }`}
                    >
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Latest events */}
            <div className="mt-5 space-y-2">
              {[
                { t: "Vessel departed Mersin · MSC AURIGA", time: "03 Jun · 14:22" },
                { t: "Container loaded MSCU1234567", time: "03 Jun · 09:10" },
                { t: "Booking confirmed by MSC", time: "01 Jun · 16:55" },
              ].map((e, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-1.5 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2.5 text-xs sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A1128]" />
                    <span className="text-[#0A1128] font-medium">{e.t}</span>
                  </div>
                  <span className="text-slate-400 font-mono">{e.time}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div className="col-span-12 bg-slate-50/30 p-5 sm:p-6 lg:col-span-5 lg:p-7">
            <div className="fiq-overline text-slate-400 mb-3">Operation Health</div>
            <div className="rounded-lg bg-white border border-slate-200 p-4 mb-4">
              <div className="flex items-end justify-between">
                <div>
                  <div className="font-display text-3xl font-bold text-emerald-600 leading-none">98</div>
                  <div className="text-[11px] text-slate-500 mt-1">Excellent</div>
                </div>
                <div className="flex items-end gap-1 h-10">
                  {[0.5, 0.7, 0.55, 0.85, 0.7, 0.95, 1].map((h, i) => (
                    <div
                      key={i}
                      className="w-1.5 rounded-sm bg-emerald-500/80"
                      style={{ height: `${h * 100}%` }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2.5">
              {[
                { k: "Carrier", v: "MSC", tone: "neutral" },
                { k: "Booking Status", v: "Confirmed", tone: "success" },
                { k: "Container", v: "Loaded", tone: "success" },
                { k: "Documents", v: "Complete", tone: "success" },
                { k: "Alerts", v: "0 Active", tone: "neutral" },
              ].map((p, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-3 py-2 rounded-md border border-slate-200 bg-white"
                >
                  <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{p.k}</span>
                  <span
                    className={`text-xs font-semibold ${
                      p.tone === "success" ? "text-emerald-700" : "text-[#0A1128]"
                    }`}
                  >
                    {p.v}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-lg bg-[#0A1128] text-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <Compass className="w-3.5 h-3.5 text-[#FF6B35]" />
                <span className="fiq-overline text-white/70">Next Action</span>
              </div>
              <div className="text-sm font-medium leading-snug">
                Confirm destination port handler with MSC by 09 June.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Side floating chip */}
      <div className="absolute -left-2 top-6 hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 shadow-[0_12px_30px_-12px_rgba(10,17,40,0.25)] xl:flex">
        <Layers className="w-3.5 h-3.5 text-[#FF6B35]" />
        <span className="text-[11px] font-semibold text-[#0A1128]">3 Workspaces synced</span>
      </div>
      <div className="absolute bottom-4 right-4 hidden items-center gap-2 rounded-full bg-[#0A1128] px-3 py-2 text-white shadow-[0_18px_30px_-12px_rgba(10,17,40,0.45)] xl:flex">
        <Container className="w-3.5 h-3.5 text-[#FF6B35]" />
        <span className="text-[11px] font-semibold tracking-wide">MSCU1234567 · loaded</span>
      </div>
    </div>
  );
};

export default HeroDashboard;
